import { ShieldCheck, Target, HeartHandshake } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { CTASection } from "../components/CTASection";
import { IMAGES, STATS } from "../data/site";

const VALUES = [
  { icon: ShieldCheck, title: "Security as a standard", body: "We treat every client's data as if it were our own — with zero-trust principles applied by default." },
  { icon: Target, title: "Proactive by design", body: "We prevent problems instead of reacting to them, so your team stays focused on billable work." },
  { icon: HeartHandshake, title: "Partnership for the long term", body: "We measure success by the years our clients stay with us, not by tickets closed." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About Zentryx Systems — Atlanta Managed IT & Cybersecurity Company"
        description="Zentryx Systems is an Atlanta-based managed IT and cybersecurity company serving small and midsize businesses, with a focus on CPA/accounting and architecture/engineering firms."
        path="/about"
      />
      <section className="pt-32 sm:pt-40 pb-16 px-5 sm:px-8 bg-mist">
        <div className="max-w-5xl mx-auto text-center">
          <Reveal>
            <p className="text-sm uppercase tracking-widest font-semibold text-teal mb-4">About Zentryx</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-charcoal leading-tight">
              Atlanta's trusted technology partner.
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Zentryx Systems was founded on a simple belief: growing businesses deserve IT that is as reliable, secure, and meticulous as the work they deliver.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 sm:py-28 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <img src={IMAGES.team} alt="Zentryx team" className="rounded-3xl w-full h-[420px] object-cover shadow-xl" />
          </Reveal>
          <Reveal delay={0.12}>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-charcoal">
              Engineered for trust, built for the long run.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              We support small and midsize businesses across the Atlanta metro, across every industry. We also bring specialized experience with accounting/CPA and architecture/engineering firms — so if that's you, we understand your seasonal pressures, your data sensitivity, and the technology that powers your work.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Our clients trust us with sensitive data and business-critical systems — and we give them access to a broader bench of engineering capability than a one-size-fits-all provider, with the discipline, transparency, and security-first mindset that responsibility demands.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 px-5 sm:px-8 bg-navy text-white">
        <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.06}>
              <p className="font-heading text-3xl sm:text-4xl font-extrabold text-white">{s.value}</p>
              <p className="text-sm text-slate-300 mt-2">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-24 sm:py-32 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto">
          <Reveal className="max-w-2xl mb-16">
            <p className="text-sm uppercase tracking-widest font-semibold text-teal mb-4">Our values</p>
            <h2 className="font-heading text-3xl sm:text-5xl font-bold tracking-tight text-charcoal">
              What it means to partner with Zentryx.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="h-full rounded-3xl border border-slate-200 bg-white p-8">
                  <div className="h-12 w-12 rounded-2xl bg-teal/10 flex items-center justify-center mb-6">
                    <v.icon className="h-6 w-6 text-teal" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-charcoal mb-2">{v.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <CTASection />
    </div>
  );
}
